<template>
  <section class="py-24 max-w-7xl mx-auto px-6 bg-white dark:bg-slate-900" id="contact" aria-labelledby="contact-heading-t5">
    <div class="grid grid-cols-1 gap-16 lg:grid-cols-2">
      <div>
        <h2 id="contact-heading-t5" class="rms-t5-heading mb-8">{{ $t('public.contactUs') }}</h2>
        <div class="space-y-6">
          <div v-for="c in contacts" :key="c.uuid" class="flex items-start gap-4">
            <span
              class="material-symbols-outlined p-2 rounded-lg shrink-0"
              :style="{ color: 'var(--rms-t5-primary, #2563eb)', backgroundColor: 'rgba(37, 99, 235, 0.1)' }"
              aria-hidden="true"
            >
              {{ contactIcon(c.type) }}
            </span>
            <div class="min-w-0">
              <p class="font-bold text-slate-900 dark:text-slate-100">{{ contactTypeLabel(c.type) }}{{ c.label ? ` — ${c.label}` : '' }}</p>
              <template v-if="isLinkType(c.type) && contactVal(c)">
                <a
                  :href="contactVal(c)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-slate-600 dark:text-slate-400 hover:opacity-90 inline-flex items-center min-h-[44px] py-2 -my-2 break-all rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  :style="{ '--tw-ring-color': 'var(--rms-t5-primary, #2563eb)' }"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <template v-else-if="c.type === 'email' && contactVal(c)">
                <a
                  :href="`mailto:${contactVal(c)}`"
                  class="text-slate-600 dark:text-slate-400 hover:opacity-90 inline-flex items-center min-h-[44px] py-2 -my-2 break-all rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  :style="{ '--tw-ring-color': 'var(--rms-t5-primary, #2563eb)' }"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <template v-else-if="c.type === 'whatsapp' && whatsAppUrl(contactVal(c))">
                <a
                  :href="whatsAppUrl(contactVal(c))"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-slate-600 dark:text-slate-400 hover:opacity-90 inline-flex items-center min-h-[44px] py-2 -my-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  :style="{ '--tw-ring-color': 'var(--rms-t5-primary, #2563eb)' }"
                >
                  {{ contactVal(c) }}
                </a>
                <span class="text-slate-500 text-sm ml-1">(WhatsApp)</span>
              </template>
              <template v-else-if="isCallable(c.type)">
                <a
                  :href="`tel:${contactVal(c).replace(/\s/g, '')}`"
                  class="text-slate-600 dark:text-slate-400 hover:opacity-90 inline-flex items-center min-h-[44px] py-2 -my-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  :style="{ '--tw-ring-color': 'var(--rms-t5-primary, #2563eb)' }"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <p v-else class="text-slate-600 dark:text-slate-400">{{ contactVal(c) }}</p>
            </div>
          </div>
          <div v-if="!contacts.length" class="text-slate-600 dark:text-slate-400">{{ $t('public.noContactListed') }}</div>
        </div>
      </div>
      <div v-if="hasSchedule" id="hours">
        <h3 id="hours-heading-t5" class="rms-t5-heading mb-6">{{ $t('public.openingHours') }}</h3>
        <PublicOpenStatusBadge :operating-hours="operatingHours" variant="contact" />
        <div class="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <table class="w-full text-left text-sm" role="table" aria-labelledby="hours-heading-t5">
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
              <template v-if="hoursSummary.is24_7">
                <tr class="bg-white dark:bg-slate-900">
                  <td class="px-6 py-4 font-medium text-slate-900 dark:text-slate-100" colspan="2">{{ $t('public.open24_7') }}</td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="row in hoursSummary.perDay"
                  :key="row.day"
                  class="bg-white dark:bg-slate-900 odd:bg-slate-50 dark:odd:bg-slate-800/50"
                >
                  <td class="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">{{ $t('public.day' + row.day.charAt(0).toUpperCase() + row.day.slice(1)) }}</td>
                  <td class="px-6 py-4 text-slate-600 dark:text-slate-400">{{ row.text === 'Closed' ? $t('public.closedNow') : row.text }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { getOperatingHoursSummary, hasOperatingHoursSchedule } from '@/utils/availability'
import { buildWhatsAppUrl, contactValue, isLinkType } from '@/utils/contact'

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  operatingHours: { type: Object, default: null },
})

const { t } = useI18n()
const hoursSummary = computed(() => getOperatingHoursSummary(props.operatingHours))
const hasSchedule = computed(() => hasOperatingHoursSchedule(props.operatingHours))

function contactVal(c) {
  return contactValue(c)
}

function contactIcon(type) {
  if (type === 'email') return 'mail'
  if (type === 'whatsapp') return 'chat'
  if (isLinkType(type)) return 'link'
  if (['mobile', 'phone', 'fax'].includes(type)) return 'call'
  return 'location_on'
}

function contactTypeLabel(type) {
  try {
    const translated = t('public.' + type)
    if (translated && translated !== 'public.' + type) return translated
  } catch (_) {}
  return type
}

function whatsAppUrl(number) {
  return buildWhatsAppUrl(number)
}

function isCallable(type) {
  return ['mobile', 'phone', 'fax'].includes(type)
}
</script>

